import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  checkIfRecaptchaEnabled,
  Form,
  TEST_GRECAPTCHA,
  TextLink,
  theme,
  useNoticeBannersStackContext,
  useReCaptcha,
} from '@sweetgreen/garnish';

import {
  useHasFailedToFetchCustomerData,
  useIsLoggedIn,
  useIsLoggingIn,
} from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import {
  useCloseActiveScreensOnAuthStatusChange,
  useRedirectAfterAuthStateChange,
} from '../../navigation';
import type { LoginScreenProps } from '../../navigation/AppNavigation.props';
import type { LoginFormData } from './hooks';
import { useLogIn, useLoginForm } from './hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const LoginScreen = ({ navigation, route }: LoginScreenProps) => {
  const {
    redirect: redirectParam,
    redirectToPlanId,
    redirectToCampaignId,
  } = route.params ?? {};
  const { customer } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const { track } = useTelemetry();

  const redirectAfterAuthStateChange = useRedirectAfterAuthStateChange();

  // ─────────────────────────────────────────────────────────────────

  const handlePressJoin = useCallback(() => {
    navigation.navigate('Join', {
      redirect: redirectParam,
      redirectToPlanId,
      redirectToCampaignId,
    });
  }, [navigation, redirectParam, redirectToPlanId, redirectToCampaignId]);

  const handlePressForgotPassword = useCallback(() => {
    navigation.navigate('ForgotPassword', { redirect: redirectParam });
  }, [navigation, redirectParam]);

  // ─────────────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    if (!isLoggedIn || !customer?.id) return;

    redirectAfterAuthStateChange();

    track('sign-in.success', { userId: customer.id });
  }, [customer?.id, isLoggedIn, redirectAfterAuthStateChange, track]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <LoginForm
      testID="login-form"
      onPressJoin={handlePressJoin}
      onPressForgotPassword={handlePressForgotPassword}
    />
  );
};

export const LoginForm = (props: LoginFormProps) => {
  const { testID, onPressJoin, onPressForgotPassword } = props;

  const isLoggingIn = useIsLoggingIn();
  const hasFailedToFetchCustomerData = useHasFailedToFetchCustomerData();

  // ────────────────────────────────────────────────────────────────────

  const { form, showVerificationErrors } = useLoginForm();
  const { control, handleSubmit, formState } = form;
  const { errors, isSubmitting } = formState;

  const {
    isFetching: isFetchingLogInResponse,
    logIn,
    hasSuccessfullyResolvedLoginMutation,
  } = useLogIn({
    onValidationError: showVerificationErrors,
  });

  // ─────────────────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── ReCaptcha ───────────────────────────────────────────────────

  const { reCaptchaMachine, reCaptchaRef } = useReCaptcha();

  const [reCaptchaState] = reCaptchaMachine;
  const isLoadingRecaptcha = reCaptchaState.matches('loading');
  const isExecutingRecaptcha = reCaptchaState.matches('executing');

  // ─── Helpers ────────────────────────────────────────────────────────

  const closeActiveScreensOnAuthStatusChange =
    useCloseActiveScreensOnAuthStatusChange();

  const signInUser = useCallback(
    async (formData: LoginFormData) => {
      const { email, password, grecaptcha = TEST_GRECAPTCHA } = formData;

      const logInResponse = await logIn({
        email,
        password,
        grecaptcha,
      });

      const logInResponseTypeName = logInResponse?.__typename;
      const isSuccessfullyLoggedIn = logInResponseTypeName === 'LoginSuccess';

      // close other open screens and modals, to prevent unnecessary network calls and state updates
      if (isSuccessfullyLoggedIn) closeActiveScreensOnAuthStatusChange();

      // reset recaptcha after request
      reCaptchaRef.current?.resetRecaptcha();
    },
    [closeActiveScreensOnAuthStatusChange, logIn, reCaptchaRef],
  );

  const onRecaptchaVerify = useCallback(
    async (grecaptcha: string) => {
      if (!grecaptcha) return;

      const formValues = form.getValues();

      await signInUser({ ...formValues, grecaptcha });
    },
    [form, signInUser],
  );

  const onRecaptchaError = useCallback(() => {
    addNoticeBanner({ text: t('general.error'), palette: 'caution' });
  }, [addNoticeBanner, t]);

  const onCustomerDataError = useCallback(() => {
    addNoticeBanner({
      text: t('login.failed-to-fetch-customer-error'),
      palette: 'caution',
    });
  }, [addNoticeBanner, t]);

  const submitForm = useMemo(() => {
    const isRecaptchaEnabled = checkIfRecaptchaEnabled();

    return handleSubmit(async (formData) => {
      await (isRecaptchaEnabled
        ? reCaptchaRef.current?.executeRecaptcha()
        : signInUser(formData));
    });
  }, [handleSubmit, reCaptchaRef, signInUser]);

  // ────────────────────────────────────────────────────────────────────

  const shouldDisableControls =
    isSubmitting ||
    isFetchingLogInResponse ||
    isLoggingIn ||
    isExecutingRecaptcha;

  // NOTE: After successful login response, the customer's profile is rarely not completely
  //       formed, resulting in an odd auth status. To prevent this we block further actions
  //       and display the corresponding error message.
  const shouldDisplayFailedCustomerDataError =
    !shouldDisableControls &&
    hasSuccessfullyResolvedLoginMutation &&
    hasFailedToFetchCustomerData;

  // ─── Effects ─────────────────────────────────────────────────────────

  // Display an error banner if retrieving customer data fails after login.
  useEffect(() => {
    if (shouldDisplayFailedCustomerDataError) onCustomerDataError();
  }, [onCustomerDataError, shouldDisplayFailedCustomerDataError]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper>
      <Form.Container
        testID={testID}
        reCaptchaRef={reCaptchaRef}
        reCaptchaMachine={reCaptchaMachine}
        onRecaptchaVerify={onRecaptchaVerify}
        onRecaptchaError={onRecaptchaError}
      >
        <Form.Title>{t('login.title')}</Form.Title>

        <Form.Subtitle>
          <BodyText size={3}>{t('login.subtitle')} </BodyText>

          <View style={styles.joinBtnWrapper}>
            <Button
              onPress={onPressJoin}
              palette="secondary"
              testID="button-join-now"
              accessibilityLabel={t('login.join-button')}
              disabled={shouldDisableControls}
            >
              {t('login.join-button')}
            </Button>
          </View>
        </Form.Subtitle>

        {/* ========== email ========== */}

        <Form.Group>
          <Form.TextField
            name="email"
            keyboardType="email-address"
            control={control}
            rules={{ required: true }}
            testID="field-email"
            label={t('form.email')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="email"
            notice={errors.email?.message}
            disabled={shouldDisableControls}
          />
        </Form.Group>

        {/* ========== password ========== */}

        <Form.Group>
          <Form.TextFieldPassword
            name="password"
            control={control}
            rules={{ required: true }}
            testID="field-password"
            label={t('form.password')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="password"
            notice={errors.password?.message}
            toggleTestID="field-password-toggle"
            disabled={shouldDisableControls}
          />
        </Form.Group>

        {/* ========== ========== ========== */}

        <Form.SubmitButton
          testID="button-sign-in"
          accessibilityLabel={t('login.sign-in-button')}
          onPress={submitForm}
          isLoading={shouldDisableControls}
          disabled={isLoadingRecaptcha}
        >
          {t('login.sign-in-button')}
        </Form.SubmitButton>

        {/* ========== ========== ========== */}

        <View style={styles.forgotPasswordWrapper}>
          <TextLink
            onPress={onPressForgotPassword}
            size={4}
            testID="link-forgot-password"
            accessibilityLabel={t('login.forgot-password-button')}
            disabled={shouldDisableControls}
          >
            {t('login.forgot-password-button')}
          </TextLink>
        </View>
      </Form.Container>
    </PageWrapper>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type LoginFormProps = Readonly<{
  onPressJoin: () => void;
  onPressForgotPassword: () => void;
}> &
  Pick<ViewProps, 'testID'>;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  joinBtnWrapper: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing['4'],
  },
  forgotPasswordWrapper: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing['10'],
  },
});
